
import { MainLayout } from "@/components/layout/MainLayout";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { VideoCard } from "@/components/videos/VideoCard";
import { AppointmentLink } from "@/components/videos/AppointmentLink";
import { CreditsDisplay } from "@/components/videos/CreditsDisplay";
import { useVideos } from "@/hooks/use-videos";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCcw } from "lucide-react";

const ResourcesVideos = () => {
  const { videos, refreshVideos, isLoading } = useVideos();
  const { userCredits, setUserCredits } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Force refresh videos when component mounts
  useEffect(() => {
    console.log("ResourcesVideos component mounted, refreshing videos");
    handleRefreshVideos();
  }, []);

  const handleRefreshVideos = async () => {
    setIsRefreshing(true);
    try {
      await refreshVideos();
      console.log("Videos refreshed successfully");
      toast({
        title: "Ενημέρωση",
        description: "Η λίστα των βίντεο ενημερώθηκε με επιτυχία.",
      });
    } catch (error) {
      console.error("Error refreshing videos:", error);
      toast({
        title: "Σφάλμα",
        description: "Παρουσιάστηκε πρόβλημα κατά την ανάκτηση των βίντεο.",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleViewVideo = (videoId: number, videoCredits: number) => {
    if (userCredits < videoCredits) {
      toast({
        title: "Ανεπαρκή Credits",
        description: "Δεν έχετε αρκετά credits για να παρακολουθήσετε αυτό το video.",
        variant: "destructive"
      });
      return;
    }

    // Deduct credits
    setUserCredits(userCredits - videoCredits);
    
    // Find the video
    const video = videos.find(v => v.id === videoId);
    if (!video) {
      toast({
        title: "Σφάλμα",
        description: "Το βίντεο δεν βρέθηκε.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Προβολή Video",
      description: `Ξεκινάει η προβολή του video «${video.title}». Αφαιρέθηκαν ${videoCredits} credits.`
    });
    
    // If video has a URL (from admin upload), open it
    if (video.url) {
      window.open(video.url, '_blank');
      console.log("Opening video URL:", video.url);
    } else {
      console.error("Video has no URL:", video);
      toast({
        title: "Σφάλμα",
        description: "Δεν βρέθηκε η διεύθυνση του βίντεο.",
        variant: "destructive"
      });
    }
  };

  return (
    <MainLayout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Εκπαιδευτικά Video</h1>
            <p className="text-muted-foreground mt-2">
              Βρείτε επαγγελματικά εκπαιδευτικά video για λογιστικά θέματα
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleRefreshVideos} 
            disabled={isRefreshing}
          >
            <RefreshCcw className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
            Ανανέωση
          </Button>
        </div>

        <CreditsDisplay credits={userCredits} />

        <AppointmentLink />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos && videos.length > 0 ? (
            videos.map((video) => (
              <VideoCard 
                key={video.id}
                video={video}
                userCredits={userCredits}
                onViewVideo={handleViewVideo}
              />
            ))
          ) : (
            <div className="col-span-3 text-center py-10">
              <p className="text-muted-foreground">
                {isLoading 
                  ? "Φόρτωση βίντεο..." 
                  : "Δεν βρέθηκαν διαθέσιμα βίντεο. Παρακαλώ επικοινωνήστε με τον διαχειριστή."}
              </p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ResourcesVideos;
